import axios from "axios";
import { API_URL } from "@/core/config/config.js";

const ACCESS_TOKEN_KEY = "driver_access_token";
const REFRESH_TOKEN_KEY = "driver_refresh_token";

const client = axios.create({
  baseURL: API_URL
});

const token = () => {
  const value = localStorage.getItem(ACCESS_TOKEN_KEY);

  return value && value !== "undefined" && value !== "null" ? value : null;
};

const refreshToken = () => {
  const value = localStorage.getItem(REFRESH_TOKEN_KEY);

  return value && value !== "undefined" && value !== "null" ? value : null;
};

const saveTokens = (data = {}) => {
  const accessToken = data.accessToken || data.access_token;
  const refreshToken = data.refreshToken || data.refresh_token;

  if (!accessToken) {
    clearTokens();
    return;
  }

  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);

  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  } else {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }
};

const clearTokens = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

const authHeaders = () => ({
  Authorization: `Bearer ${token()}`
});

const refreshAccessToken = () => {
  const currentRefreshToken = refreshToken();

  if (!currentRefreshToken) {
    return Promise.reject(new Error("Missing driver refresh token"));
  }

  return client
    .post(
      "driver/refresh",
      { refreshToken: currentRefreshToken },
      { skipDriverRefresh: true }
    )
    .then(({ data }) => {
      saveTokens(data.data);

      return data;
    });
};

client.interceptors.response.use(
  response => response,
  error => {
    const response = error.response;
    const config = response ? response.config : null;

    if (
      !response ||
      response.status !== 401 ||
      !config ||
      config.skipDriverRefresh ||
      config.driverRefreshRetried
    ) {
      return Promise.reject(error);
    }

    config.driverRefreshRetried = true;

    return refreshAccessToken()
      .then(() => {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token()}`;

        return client(config);
      })
      .catch(refreshError => {
        clearTokens();

        return Promise.reject(refreshError);
      });
  }
);

export default {
  hasToken() {
    return !!token();
  },

  login(payload) {
    return client
      .post("driver/login", payload, { skipDriverRefresh: true })
      .then(({ data }) => {
        saveTokens(data.data);
        return data;
      });
  },

  serviceDashboard(params = {}) {
    return client.get("driver/service-dashboard", {
      headers: authHeaders(),
      params
    });
  },

  createSignal(payload) {
    return client.post("driver/machine-service-signal", payload, {
      headers: authHeaders()
    });
  },

  refreshToken: refreshAccessToken,

  logout() {
    return client
      .post("driver/logout", {}, {
        headers: authHeaders(),
        skipDriverRefresh: true
      })
      .finally(clearTokens);
  },

  clearTokens
};
